import type {VoidFunctionComponent} from "react";
import React, {useState} from "react";
import s from "./ToggleDocumentation.module.scss";
import Toggle from "../../../components/common/Toggle";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";

export interface IToggleDocumentationProps {
}

export const ToggleDocumentation: VoidFunctionComponent<IToggleDocumentationProps> = ({}) => {
  let propertyIndex = 0;
  
  // State hooks for interactive examples
  const [isToggled1, setIsToggled1] = useState(false);
  const [isToggled2, setIsToggled2] = useState(true);
  const [isToggled3, setIsToggled3] = useState(false);
  const [isToggled4, setIsToggled4] = useState(false);
  const [isToggled5, setIsToggled5] = useState(false);
  const [isToggled6, setIsToggled6] = useState(false);
  const [isToggled7, setIsToggled7] = useState(false);

  const properties: IPropertyWrapper[] = [
    {
      isRequired: true,
      name: "isToggledOn",
      element: <Property
        key={++propertyIndex}
        name={"isToggledOn"}
        description={"Boolean that determines whether the toggle is in the 'on' or 'off' state."}
        isRequired={true}
        type={"boolean"}
        examples={<>
          <Example title={'isToggledOn = false'}>
            <Toggle
              isToggledOn={isToggled1}
              onToggle_toggled={() => setIsToggled1((is) => !is)}
              label="Click me"
            />
          </Example>
          <br />
          <Example title={'isToggledOn = true'}>
            <Toggle
              isToggledOn={isToggled2}
              onToggle_toggled={() => setIsToggled2((is) => !is)}
              label="Click me too"
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: true,
      name: "toggleFunc",
      element: <Property
        key={++propertyIndex}
        name={"toggleFunc"}
        description={"A javascript function that is called when the toggle is clicked. Typically this function toggles the state. Clicking the label also triggers this function."}
        isRequired={true}
        type={"() => void"}
        examples={<>
          <Example title={'toggleFunc handler'}>
            <Toggle
              isToggledOn={isToggled3}
              onToggle_toggled={() => {
                setIsToggled3((is) => !is);
                alert('A state change is scheduled. The component will re-render in the toggled state soon.');
              }}
              label="Toggle me"
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "label",
      element: <Property
        key={++propertyIndex}
        name={"label"}
        description={"Optional text label that appears to the left of the toggle switch."}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={'label = "Enable feature"'}>
            <Toggle
              isToggledOn={isToggled4}
              onToggle_toggled={() => setIsToggled4((is) => !is)}
              label="Enable feature"
            />
          </Example>
          <br />
          <Example title={'No label provided'}>
            <Toggle
              isToggledOn={isToggled5}
              onToggle_toggled={() => setIsToggled5((is) => !is)}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "disabled",
      element: <Property
        key={++propertyIndex}
        name={"disabled"}
        description={<>
          When true, prevents the toggle from being clicked and applies a disabled visual style.
          <span className={s.warning}> NOTE: The toggle will not respond to clicks when disabled.</span>
        </>}
        isRequired={false}
        type={"boolean"}
        examples={<>
          <Example title={'disabled = true'}>
            <Toggle
              isToggledOn={isToggled6}
              onToggle_toggled={() => setIsToggled6((is) => !is)}
              label="Disabled toggle"
              disabled={true}
            />
          </Example>
          <br />
          <Example title={'disabled = false (default)'}>
            <Toggle
              isToggledOn={isToggled7}
              onToggle_toggled={() => setIsToggled7((is) => !is)}
              label="Enabled toggle"
              disabled={false}
            />
          </Example>
        </>}
      />
    },
  ];

  sortProperties(properties);

  const [containerToggle1, setContainerToggle1] = useState(false);
  const [containerToggle2, setContainerToggle2] = useState(false);
  const [containerToggle3, setContainerToggle3] = useState(false);

  return <div className={s.container}>
    <ComponentDescription
      name={"Toggle"}
      description={"A toggle switch allows users to turn an option on or off. It provides a clear visual representation of binary states."}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}

    <hr className={s.sectionDivider} />

    <section className={s.layoutSection}>
      <h2>Layout Behavior</h2>
      <p className={s.sectionDescription}>
        The Toggle component expands to fill the width of its container. The label takes up available space on the left,
        while the switch remains fixed-width on the right.
      </p>

      <Example title={'Toggle in a narrow container (200px)'}>
        <div className={s.narrowContainer}>
          <Toggle
            isToggledOn={containerToggle1}
            onToggle_toggled={() => setContainerToggle1((is) => !is)}
            label="Narrow"
          />
        </div>
      </Example>
      <br />

      <Example title={'Toggle in a medium container (400px)'}>
        <div className={s.mediumContainer}>
          <Toggle
            isToggledOn={containerToggle2}
            onToggle_toggled={() => setContainerToggle2((is) => !is)}
            label="Medium width"
          />
        </div>
      </Example>
      <br />

      <Example title={'Toggle in a wide container (600px)'}>
        <div className={s.wideContainer}>
          <Toggle
            isToggledOn={containerToggle3}
            onToggle_toggled={() => setContainerToggle3((is) => !is)}
            label="Wide container with longer label text"
          />
        </div>
      </Example>
      <br />

      <Example title={'Multiple toggles stacked vertically expand to container width. Relatively minimal spacing exists between toggles.'}>
        <div className={s.mediumContainer}>
          <Toggle
            isToggledOn={false}
            onToggle_toggled={() => {}}
            label="First option"
          />
          <Toggle
            isToggledOn={true}
            onToggle_toggled={() => {}}
            label="Second option"
          />
          <Toggle
            isToggledOn={false}
            onToggle_toggled={() => {}}
            label="Third option"
          />
        </div>
      </Example>
    </section>
  </div>;
}

